import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useCorrectiveActions, useUpdateCorrectiveAction } from '@/hooks/use-corrective-actions';
import { formatDateOnly } from '@/lib/date-utils';
import type { CorrectiveActionFilters, CorrectiveActionStatus } from '@/lib/types';

const STATUS_LABELS: Record<CorrectiveActionStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  pending_verification: 'Pending Verification',
  closed: 'Closed',
  overdue: 'Overdue',
};

const STATUS_VARIANTS: Record<CorrectiveActionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  open: 'default',
  in_progress: 'secondary',
  pending_verification: 'outline',
  closed: 'secondary',
  overdue: 'destructive',
};

export function ActionsPage() {
  const [filters, setFilters] = useState<CorrectiveActionFilters>({});
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCorrectiveActions({ ...filters, search: search || undefined });
  const updateAction = useUpdateCorrectiveAction();

  const actions = data?.results ?? [];

  function handleStatusChange(id: number, newStatus: CorrectiveActionStatus) {
    updateAction.mutate({ id, status: newStatus });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">CAPA Register</h1>
          <p className="text-muted-foreground">
            Corrective &amp; Preventive Actions
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Input
              placeholder="Search title or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <Select
              value={filters.status ?? 'all'}
              onValueChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  status: v === 'all' ? undefined : (v as CorrectiveActionStatus),
                }))
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_verification">Pending Verification</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.action_type ?? 'all'}
              onValueChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  action_type: v === 'all' ? undefined : (v as 'corrective' | 'preventive'),
                }))
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="corrective">Corrective</SelectItem>
                <SelectItem value="preventive">Preventive</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.source_type ?? 'all'}
              onValueChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  source_type: v === 'all' ? undefined : (v as CorrectiveActionFilters['source_type']),
                }))
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="incident">Incident</SelectItem>
                <SelectItem value="audit_finding">Audit Finding</SelectItem>
                <SelectItem value="management_review">Management Review</SelectItem>
                <SelectItem value="alert">Alert</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="nc_assessment">NC Assessment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stacked cards on narrow screens (≤480px) */}
      <div className="space-y-3 min-[481px]:hidden">
        {isLoading ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Loading...
            </CardContent>
          </Card>
        ) : actions.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No corrective actions found
            </CardContent>
          </Card>
        ) : (
          actions.map((action) => (
            <Card key={action.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <Link
                    to={`/compliance/actions/${action.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {action.title}
                  </Link>
                  <Badge variant={STATUS_VARIANTS[action.status as CorrectiveActionStatus]}>
                    {STATUS_LABELS[action.status as CorrectiveActionStatus]}
                  </Badge>
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                      Clause
                    </dt>
                    <dd className="mt-0.5">
                      {action.clause_ref ? (
                        <span className="font-mono text-xs" title={action.clause_title ?? undefined}>
                          {action.clause_ref}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                      Type
                    </dt>
                    <dd className="mt-0.5 capitalize">{action.action_type}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                      Source
                    </dt>
                    <dd className="mt-0.5 capitalize">
                      {action.source_type.replace('_', ' ')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                      Assigned To
                    </dt>
                    <dd className="mt-0.5">{action.assigned_to_username ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                      Due Date
                    </dt>
                    <dd className="mt-0.5">{formatDateOnly(action.due_date)}</dd>
                  </div>
                </dl>

                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Change Status
                  </label>
                  <Select
                    value={action.status}
                    onValueChange={(v) =>
                      handleStatusChange(action.id, v as CorrectiveActionStatus)
                    }
                  >
                    <SelectTrigger className="mt-1 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending_verification">Pending Verification</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Table on wider screens */}
      <Card className="hidden min-[481px]:block">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Clause</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[180px]">Change Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : actions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No corrective actions found
                  </TableCell>
                </TableRow>
              ) : (
                actions.map((action) => (
                  <TableRow key={action.id}>
                    <TableCell>
                      <Link
                        to={`/compliance/actions/${action.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {action.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {action.clause_ref ? (
                        <span className="text-xs font-mono" title={action.clause_title ?? undefined}>
                          {action.clause_ref}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="capitalize">{action.action_type}</TableCell>
                    <TableCell className="capitalize">
                      {action.source_type.replace('_', ' ')}
                    </TableCell>
                    <TableCell>{action.assigned_to_username ?? '—'}</TableCell>
                    <TableCell>{formatDateOnly(action.due_date)}</TableCell>
                    <TableCell>
                        <Badge variant={STATUS_VARIANTS[action.status as CorrectiveActionStatus]}>
                          {STATUS_LABELS[action.status as CorrectiveActionStatus]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={action.status}
                        onValueChange={(v) =>
                          handleStatusChange(action.id, v as CorrectiveActionStatus)
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="pending_verification">Pending Verification</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data && data.count > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {actions.length} of {data.count} actions
          </span>
          <div className="flex gap-2">
            {data.previous && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters((f) => ({ ...f, page: (f.page ?? 2) - 1 }))
                }
              >
                Previous
              </Button>
            )}
            {data.next && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))
                }
              >
                Next
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
